import { formatUtcOffset } from '@felix/domain';
import {
  IconAlertTriangleFilled,
  IconCertificateOff,
  IconClockEdit,
  IconClockFilled,
  IconFileTimeFilled,
  IconLifebuoy,
  IconShieldCheck,
  IconShieldFilled,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../EmptyState.js';
import { formatDay } from '../format.js';
import classes from './AlertList.module.css';
import type { DashboardAlert } from './dashboard-types.js';

/** Çalışma ortamının bugünkü yerel günü `YYYY-MM-DD`. */
function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** İki gün arasındaki gün farkı (`YYYY-MM-DD`; saat dilimi kaydırması yok). */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Uyarı listesi (personel-mlc-calisma-dinlenme.jpeg "Active Alerts"): türe göre dolu ikon, tek satır
 * ileti, kişi ve tarih; `onOpen` verilirse sağda "View". Zilin açılan penceresinde ve panoda aynı
 * bileşen (M6f G1-09).
 */
export function AlertList({
  alerts,
  limit,
  formatDate = formatDay,
  today,
  onOpen,
}: {
  alerts: readonly DashboardAlert[];
  limit?: number;
  /** Gün biçimi; varsayılan `GG.AA.YYYY` (M6f G1-04). */
  formatDate?: (date: string) => string;
  /** Dolacak belgenin gün sayısı için bugün (`YYYY-MM-DD`, gemi günü); verilmezse yerel gün. */
  today?: string;
  /** Uyarının ekranını açar (ör. kişinin belgeleri); verilirse satırda "View". */
  onOpen?: (alert: DashboardAlert) => void;
}) {
  const { t } = useTranslation();
  const shown = limit === undefined ? alerts : alerts.slice(0, limit);
  if (shown.length === 0) {
    return (
      <EmptyState
        className={classes.empty}
        icon={<IconShieldCheck size={22} stroke={1.8} />}
        title={t('alerts.none')}
      />
    );
  }
  const day = today ?? localToday();
  return (
    <ul className={classes.list}>
      {shown.map((alert) => {
        const tone =
          (alert.kind === 'certificate' && alert.status === 'expired') ||
          (alert.kind === 'rest' && alert.severity === 'violation') ||
          (alert.kind === 'contract' && alert.status === 'overdue') ||
          (alert.kind === 'drill' && alert.status === 'overdue')
            ? 'danger'
            : 'warning';
        const Icon =
          alert.kind === 'drill'
            ? IconLifebuoy
            : alert.kind === 'clock'
              ? IconClockEdit
              : alert.kind === 'contract'
                ? IconFileTimeFilled
                : alert.kind === 'rest'
                  ? IconClockFilled
                  : alert.kind === 'missing'
                    ? IconCertificateOff
                    : alert.status === 'expired'
                      ? IconShieldFilled
                      : IconAlertTriangleFilled;
        const days =
          (alert.kind === 'certificate' && alert.status === 'expiring') ||
          (alert.kind === 'contract' && alert.status === 'warning')
            ? daysBetween(day, alert.date)
            : null;
        const text =
          alert.kind === 'drill'
            ? // Tatbikat (ADR 0128 Faz 2 Ek; ADR 0156 hata düzeltmesi)
              t(`alerts.drill.${alert.status}`, { drill: alert.templateName })
            : alert.kind === 'clock'
              ? t(`alerts.clock.${alert.status}`, {
                  change: t('shipClock.minutes', {
                    value: `${alert.changeMin < 0 ? '−' : '+'}${Math.abs(alert.changeMin)}`,
                  }),
                  time: new Date(Date.parse(alert.effectiveAt) + alert.oldOffsetMin * 60_000)
                    .toISOString()
                    .slice(11, 16),
                  from: formatUtcOffset(alert.oldOffsetMin),
                  to: formatUtcOffset(alert.newOffsetMin),
                })
              : alert.kind === 'certificate'
                ? days !== null && days === 0
                  ? t('alerts.certificate.expiresToday', { certificate: alert.certificate })
                  : days !== null && days > 0
                    ? t('alerts.certificate.expiresIn', {
                        certificate: alert.certificate,
                        count: days,
                      })
                    : t(`alerts.certificate.${alert.status}`, { certificate: alert.certificate })
                : alert.kind === 'contract'
                  ? // Kontrat / MLC (ADR 0076 §4): kalan gün, bugün ya da geçmiş
                    alert.status === 'overdue'
                    ? t(`alerts.contract.${alert.basis}.overdue`)
                    : days !== null && days > 0
                      ? t(`alerts.contract.${alert.basis}.in`, { count: days })
                      : t(`alerts.contract.${alert.basis}.today`)
                  : alert.kind === 'missing'
                    ? t('alerts.missing', { count: alert.count })
                    : t(`alerts.rest.${alert.severity}`);
        const date = 'date' in alert ? formatDate(alert.date) : null;
        return (
          <li
            key={`${alert.kind}-${alert.userId}-${'certificate' in alert ? alert.certificate : ''}-${'date' in alert ? alert.date : ''}-${'changeId' in alert ? alert.changeId : ''}-${'planId' in alert ? `${alert.planId}-${alert.status}` : ''}`}
            className={classes.item}
          >
            <span className={classes.icon} data-tone={tone} aria-hidden>
              <Icon size={18} stroke={2} />
            </span>
            <span className={classes.body}>
              <span className={classes.text} title={text}>
                {text}
              </span>
              <span className={classes.meta}>
                {alert.fullName}
                {date ? ` | ${date}` : ''}
              </span>
            </span>
            {onOpen ? (
              <button
                type="button"
                className={classes.view}
                aria-label={`${t('common.view')}: ${text}`}
                onClick={() => onOpen(alert)}
              >
                {t('common.view')}
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
